import React, { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { X, Sparkle, UploadSimple, Prohibit, Image as ImageIcon } from "@phosphor-icons/react";

const MODES = [
  { id: "NONE", label: "No design", icon: Prohibit, desc: "Just a title and cap." },
  { id: "SYSTEM_GENERATED", label: "AI-generated", icon: Sparkle, desc: "Nano Banana / GPT Image 1" },
  { id: "UPLOAD", label: "Upload", icon: UploadSimple, desc: "PNG/JPG under 2MB" },
];

function readFileAsDataUrl(file, maxMB = 5) {
  return new Promise((resolve, reject) => {
    if (file.size > maxMB * 1024 * 1024) {
      reject(new Error(`Max ${maxMB}MB`));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function CreateEventDialog({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [ticketCap, setTicketCap] = useState(100);
  const [venue, setVenue] = useState("");
  const [date, setDate] = useState("");
  const [host, setHost] = useState("");
  const [description, setDescription] = useState("");
  const [designMode, setDesignMode] = useState("NONE");
  const [designUrl, setDesignUrl] = useState(null);
  const [posterUrl, setPosterUrl] = useState(null);
  const [modelProvider, setModelProvider] = useState("gemini");
  const [artStyle, setArtStyle] = useState("");
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const reset = () => {
    setTitle(""); setTicketCap(100); setVenue(""); setDate(""); setHost(""); setDescription("");
    setDesignMode("NONE"); setDesignUrl(null); setPosterUrl(null); setModelProvider("gemini");
    setArtStyle("");
  };

  const generate = async () => {
    if (!title.trim()) return toast.error("Enter a title first");
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/ticket-design", { title, model_provider: modelProvider, art_style: artStyle });
      setDesignUrl(data.design_url);
      toast.success("Design generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const onDesignFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await readFileAsDataUrl(f, 3);
      setDesignUrl(url);
    } catch (err) {
      toast.error(err.message);
    }
  };
  const onPosterFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await readFileAsDataUrl(f, 6);
      setPosterUrl(url);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const submit = async (e, opts = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!title.trim()) return toast.error("Title required");
    setSubmitting(true);
    try {
      await api.post("/events", {
        title,
        ticket_cap: Number(ticketCap),
        venue,
        date: date || null,
        host: host || null,
        description,
        art_style: artStyle,
        is_draft: !!opts.asDraft,
        design_mode: designMode,
        design_url: designMode === "NONE" ? null : designUrl,
        poster_url: posterUrl,
      });
      toast.success(opts.asDraft ? "Draft saved" : "Event created");
      reset();
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Creation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-start md:items-center justify-center p-4 overflow-auto" data-testid="create-event-dialog">
      <div className="w-full max-w-3xl bg-[#0A0A0C] border border-white/15 my-8">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Event Orchestration Agent</div>
            <div className="heading-font text-2xl font-black tracking-tighter mt-1">Create Event</div>
          </div>
          <button onClick={onClose} data-testid="close-create-dialog" className="p-2 hover:bg-white/5 transition-colors duration-200">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Title</label>
              <input required data-testid="event-title" value={title} onChange={(e) => setTitle(e.target.value)}
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200"
                placeholder="Neon Circuit · Vol. 8" />
            </div>
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Ticket Cap</label>
              <input required type="number" min={1} data-testid="event-cap" value={ticketCap} onChange={(e) => setTicketCap(e.target.value)}
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
            </div>
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Venue</label>
              <input data-testid="event-venue" value={venue} onChange={(e) => setVenue(e.target.value)}
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
            </div>
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Date</label>
              <input type="date" data-testid="event-date" value={date} onChange={(e) => setDate(e.target.value)}
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
            </div>
            <div className="md:col-span-2">
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Host (optional — defaults to your name)</label>
              <input data-testid="event-host" value={host} onChange={(e) => setHost(e.target.value)}
                placeholder="e.g. DJ Vega · Neon Collective"
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
            </div>
          </div>
          <div>
            <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Description</label>
            <textarea data-testid="event-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
              className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
          </div>

          {/* Poster upload */}
          <div>
            <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3 flex items-center gap-2">
              <ImageIcon size={12} /> Event Poster (for social sharing)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              <div className="md:col-span-2 border border-dashed border-white/15 p-6">
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">
                  <UploadSimple size={14} weight="bold" />
                  <span>{posterUrl ? "Replace poster" : "Upload poster"}</span>
                  <input type="file" accept="image/*" onChange={onPosterFile} data-testid="poster-upload" className="hidden" />
                </label>
                <div className="mono-font text-[10px] text-white/40 mt-2">Max 6MB · PNG / JPG · Recommended 1080x1350 (portrait) or 1080x1080 (square)</div>
              </div>
              {posterUrl && (
                <div className="border border-white/10 p-2">
                  <img src={posterUrl} alt="poster preview" className="w-full aspect-[4/5] object-cover" />
                  <button type="button" onClick={() => setPosterUrl(null)} data-testid="poster-remove"
                    className="mt-2 w-full mono-font text-[10px] uppercase tracking-widest text-white/50 hover:text-[#FF0033] transition-colors duration-200">
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Ticket design */}
          <div>
            <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">Ticket Design (art on the ticket)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {MODES.map((m) => {
                const Icon = m.icon;
                const active = designMode === m.id;
                return (
                  <button
                    type="button"
                    key={m.id}
                    onClick={() => setDesignMode(m.id)}
                    data-testid={`design-mode-${m.id}`}
                    className={`p-4 border text-left transition-colors duration-200 ${
                      active ? "border-[#00FF66] bg-[#00FF66]/5" : "border-white/15 hover:border-white/40"
                    }`}
                  >
                    <Icon size={18} weight="duotone" className={active ? "text-[#00FF66]" : "text-white/70"} />
                    <div className="mono-font text-xs mt-2 uppercase tracking-widest">{m.label}</div>
                    <div className="mono-font text-[10px] text-white/40 mt-1">{m.desc}</div>
                  </button>
                );
              })}
            </div>

            {designMode === "SYSTEM_GENERATED" && (
              <div className="mt-4 border border-white/10 p-4">
                <input value={artStyle} onChange={(e) => setArtStyle(e.target.value)}
                  data-testid="event-art-style"
                  placeholder="Style prompt · e.g. cyberpunk, neon minimal, vintage 90s"
                  className="w-full bg-transparent border border-white/15 px-3 py-2 mono-font text-xs mb-3 focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
                <div className="flex flex-wrap items-center gap-3">
                  <select value={modelProvider} onChange={(e) => setModelProvider(e.target.value)}
                    data-testid="design-provider"
                    className="bg-[#050505] border border-white/15 px-3 py-2 mono-font text-xs focus:outline-none focus:border-[#00FF66]">
                    <option value="gemini">Gemini Nano Banana</option>
                    <option value="openai">OpenAI GPT Image 1</option>
                  </select>
                  <button type="button" onClick={generate} disabled={generating || !title}
                    data-testid="design-generate-btn"
                    className="px-4 py-2 bg-[#9D00FF] text-white mono-font text-xs uppercase tracking-[0.25em] hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
                    {generating ? "Generating…" : "Generate Art"}
                  </button>
                </div>
                {designUrl && (
                  <img src={designUrl} alt="ticket design" className="mt-4 w-full max-w-sm border border-white/10" />
                )}
              </div>
            )}
            {designMode === "UPLOAD" && (
              <div className="mt-4 border border-white/10 p-4">
                <input type="file" accept="image/*" onChange={onDesignFile} data-testid="design-upload"
                  className="mono-font text-xs" />
                {designUrl && <img src={designUrl} alt="upload" className="mt-4 w-full max-w-sm border border-white/10" />}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} data-testid="cancel-create"
              className="px-4 py-2 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">
              Cancel
            </button>
            <button type="button" onClick={(e) => submit(e, { asDraft: true })} disabled={submitting} data-testid="save-draft"
              className="px-5 py-2 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-[#9D00FF] hover:text-[#9D00FF] transition-colors duration-200 disabled:opacity-50">
              {submitting ? "Saving…" : "Save as Draft"}
            </button>
            <button type="submit" disabled={submitting} data-testid="submit-create"
              className="px-5 py-2 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200 disabled:opacity-60">
              {submitting ? "Creating…" : "Initialize Event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
